import is from '@sindresorhus/is';
import { endOfDay, format, isAfter, isValid, parse, startOfDay } from 'date-fns';

export type DateFilterRange = [Date | null, Date | null];
export type DateFilterBound = 'end' | 'start';

export const DATE_FILTER_INPUT_FORMAT = 'dd.MM.yyyy';
export const DATE_FILTER_INPUT_PLACEHOLDER = 'dd.mm.yyyy';

const SEPARATOR = '.';
const GROUP_LENGTHS = [2, 2, 4] as const;
const MAX_DIGITS = 8;

// Builds the draft shown in the input from whatever was typed: only digits count, and the separator is inserted
// automatically once the day or month is complete. Deleting never re-adds a separator so backspace can walk past it.
export const formatDateFilterDraft = (previous: string, next: string): string => {
    const digits = next.replaceAll(/\D/gu, '').slice(0, MAX_DIGITS);
    const isDeleting = next.length < previous.length;
    const groups: string[] = [];
    let rest = digits;

    for (const length of GROUP_LENGTHS) {
        if (rest.length === 0) break;
        groups.push(rest.slice(0, length));
        rest = rest.slice(length);
    }

    const draft = groups.join(SEPARATOR);
    const lastGroup = groups.at(-1);
    const lastGroupIsCompleteDayOrMonth =
        is.string(lastGroup) &&
        groups.length < GROUP_LENGTHS.length &&
        lastGroup.length === GROUP_LENGTHS[groups.length - 1];

    return !isDeleting && lastGroupIsCompleteDayOrMonth ? `${draft}${SEPARATOR}` : draft;
};

export const formatDateFilterInput = (value: Date | null | undefined): string =>
    is.date(value) ? format(value, DATE_FILTER_INPUT_FORMAT) : '';

// Returns the local calendar day the text describes, or null while it is empty, partial, or invalid.
export const parseCompleteDateFilterInput = (value: string): Date | null => {
    const parsed = parse(value, DATE_FILTER_INPUT_FORMAT, new Date());

    return isValid(parsed) && format(parsed, DATE_FILTER_INPUT_FORMAT) === value ? parsed : null;
};

// Bounds are local calendar days: start at midnight, end at the last millisecond of the day, so a
// same-day range includes everything from that day. A bound typed past the other one wins and clears it,
// so the date stays in the field it was typed into.
export const updateDateFilterRange = (
    [currentStart, currentEnd]: DateFilterRange,
    bound: DateFilterBound,
    value: Date | null,
): DateFilterRange | undefined => {
    const [start, end] = bound === 'start' ? [value, currentEnd] : [currentStart, value];
    const crossed = is.date(start) && is.date(end) && isAfter(startOfDay(start), endOfDay(end));
    const [keptStart, keptEnd] = crossed ? (bound === 'start' ? [start, null] : [null, end]) : [start, end];
    const range: DateFilterRange = [
        is.date(keptStart) ? startOfDay(keptStart) : null,
        is.date(keptEnd) ? endOfDay(keptEnd) : null,
    ];

    return range.some((date) => is.date(date)) ? range : undefined;
};
